import React, {useCallback} from 'react'
import {useIntl} from 'react-intl'
import {useSelector} from 'react-redux'
import {BookingOptionsFilter} from 'components/Filters/BookingOptionsFilter'
import {DealFreezeFilter} from 'components/Filters/DealFreezeFilter'
import {ExclusiveFilter} from 'components/Filters/ExclusiveFilter'
import {FacetsRenderCheckboxList} from 'components/Filters/FacetFilter/FacetsRenderCheckboxList'
import {FacetsRenderPills} from 'components/Filters/FacetFilter/FacetsRenderPills'
import {filterMessages} from 'components/Filters/filterMessages'
import {MealPlansFilter} from 'components/Filters/MealPlansFilter'
import {NeighborhoodFilter} from 'components/Filters/NeighborhoodFilter'
import {PopularFilters} from 'components/Filters/PopularFilters'
import {PriceFilter} from 'components/Filters/PriceFilter/PriceFilter'
import {PropertyBrandFilter} from 'components/Filters/PropertyBrandFilter'
import {PropertyTypes} from 'components/Filters/PropertyTypes'
import {RecentlyUsedFilters} from 'components/Filters/RecentlyUsedFilters'
import {ReviewScoreFilter} from 'components/Filters/ReviewScoreFilter/ReviewScoreFilter'
import {SelectedFilters} from 'components/Filters/SelectedFilters'
import {StarRatingFilter} from 'components/Filters/StarRatingFilter'
import {buildDataId, RecentlyUsedFilter} from 'components/Filters/utils'
import {useFiltersForm} from 'hooks/useFiltersForm'
import {useFreezableFilter} from 'hooks/useFreezableFilter'
import {useOtaMode} from 'hooks/useOtaMode/useOtaMode'
import {usePriceFilter} from 'hooks/usePriceFilter'
import {getNeighborhoods, getPropertyBrands} from 'modules/sapiSearch/selectors'
import {searchParamToFacet} from 'modules/search/constants'
import {getIsCheckInAtLeastTwoDaysAway} from 'modules/search/selectors'
import {FilterUrlParams} from 'modules/search/types'
import {toArray} from 'utils/object'

import {useDeviceLayout} from '@vio/atlas/src/context/deviceLayout'
import {useBrand} from '@vio/core/src/_web/brand/hooks/useBrand'
import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'

import {FilterSection} from '../FilterSection'
import {SectionTracker} from '../SectionTracker'

const facilitiesKey = searchParamToFacet.facilities.param
const themesKey = searchParamToFacet.themes.param
const popularKey = 'popular'

interface Props
  extends Pick<
    ReturnType<typeof useFiltersForm>,
    | 'getFilters'
    | 'getAppliedFilters'
    | 'toggleMultiFilter'
    | 'toggleFilters'
    | 'getFiltersCount'
    | 'setMultiFilters'
    | 'removeMultiFilters'
    | 'getAppliedFiltersCount'
    | 'getAppliedStarsLabel'
  > {
  recentlyUsedFilters: RecentlyUsedFilter[]
}
export const AllFiltersContent: React.FC<Props> = ({
  getAppliedFilters,
  getFilters,
  toggleFilters,
  toggleMultiFilter,
  setMultiFilters,
  removeMultiFilters,
  getAppliedFiltersCount,
  getAppliedStarsLabel,
  recentlyUsedFilters
}) => {
  const {isMobile} = useDeviceLayout()
  const {formatMessage} = useIntl()
  const {brandName} = useBrand()
  const {
    monetisation: {onlyOnBrandTag}
  } = useBrandCapabilities()
  const {isOTAMode} = useOtaMode()

  const filters = getFilters() ?? {}
  const appliedFilters = getAppliedFilters() ?? {}

  const isCheckInAtLeastTwoDaysAway = useSelector(
    getIsCheckInAtLeastTwoDaysAway
  )

  const neighborhoods = useSelector(getNeighborhoods)
  const propertyBrands = useSelector(getPropertyBrands)

  const {title: priceFilterTitle, subtitle: priceFilterSubtitle} =
    usePriceFilter()

  const handleSingleFilterChange = useCallback(
    (value: FilterUrlParams) => {
      toggleFilters({
        ...value
      })
    },
    [toggleFilters]
  )

  const handleMultiFilterChange = useCallback(
    (
      filterKey: keyof FilterUrlParams,
      filterValue: string | null | undefined
    ) => {
      toggleMultiFilter(filterKey, filterValue)
    },
    [toggleMultiFilter]
  )

  const hasAppliedFilters = getAppliedFiltersCount() > 0

  // Added as a part of c747927f-deal-freeze-srp
  const isFreezableFilterEnabled = useFreezableFilter()

  return (
    <div>
      {/* Selected Filters */}
      {hasAppliedFilters && (
        <FilterSection
          dataId={buildDataId('selected')}
          title={formatMessage(filterMessages.selected)}
        >
          <SelectedFilters
            filters={filters}
            appliedFilters={appliedFilters}
            toggleMultiFilter={toggleMultiFilter}
            toggleFilters={toggleFilters}
          />
        </FilterSection>
      )}
      <SectionTracker>
        {/* Recently used filters */}
        {recentlyUsedFilters?.length > 0 && (
          <FilterSection
            dataId={buildDataId('recentlyUsed')}
            title={formatMessage(filterMessages.recentlyUsed)}
          >
            <RecentlyUsedFilters
              storedFilters={recentlyUsedFilters}
              filters={filters}
              appliedFilters={appliedFilters}
              toggleMultiFilter={toggleMultiFilter}
              toggleFilters={toggleFilters}
            />
          </FilterSection>
        )}
        {/* Popular filters */}
        {
          <FilterSection
            dataId={buildDataId(popularKey)}
            title={formatMessage(filterMessages.popularTypes)}
          >
            <PopularFilters
              filters={filters}
              appliedFilters={appliedFilters}
              toggleMultiFilter={toggleMultiFilter}
              toggleFilters={toggleFilters}
              getAppliedStarsLabel={getAppliedStarsLabel}
            />
          </FilterSection>
        }
        {/* Booking options */}
        {isCheckInAtLeastTwoDaysAway && (
          <FilterSection
            dataId={buildDataId('bookingOptions')}
            title={formatMessage(filterMessages.bookingOptions)}
          >
            <BookingOptionsFilter
              filters={filters}
              handleSingleFilterChange={handleSingleFilterChange}
            />
          </FilterSection>
        )}
        {/* Deal freeze */}
        {isFreezableFilterEnabled && (
          <FilterSection
            dataId={buildDataId('freezeDeal')}
            title={formatMessage(filterMessages.freezeDeal)}
          >
            <DealFreezeFilter
              filters={filters}
              handleSingleFilterChange={handleSingleFilterChange}
            />
          </FilterSection>
        )}
        {/* Price */}
        <FilterSection
          dataId={buildDataId('priceFilter')}
          title={formatMessage(priceFilterTitle)}
          subtitle={formatMessage(priceFilterSubtitle)}
        >
          <PriceFilter
            priceMin={filters.priceMin}
            priceMax={filters.priceMax}
            onChange={handleSingleFilterChange}
          />
        </FilterSection>
        {/* Amenities */}
        <FilterSection
          dataId={buildDataId(facilitiesKey)}
          title={formatMessage(filterMessages.amenities)}
          hasShowMore
        >
          <FacetsRenderPills
            facet="facilities"
            filters={filters.facilities}
            appliedFilters={appliedFilters.facilities}
            toggleMultiFilter={toggleMultiFilter}
            hasAppliedFilters={hasAppliedFilters}
          />
        </FilterSection>
        {/* Meal plans */}
        <FilterSection
          dataId={buildDataId('mealPlans')}
          title={formatMessage(filterMessages.mealPlans)}
        >
          <MealPlansFilter
            amenities={filters.amenities}
            onChange={toggleMultiFilter}
          />
        </FilterSection>

        {/* Exclusive filter */}
        {onlyOnBrandTag.enabled && !isOTAMode && (
          <FilterSection
            dataId={buildDataId('onlyOnBrandTag')}
            title={formatMessage(onlyOnBrandTag.config.filterSectionLabel, {
              brandName
            })}
          >
            <ExclusiveFilter filters={filters} toggleFilters={toggleFilters} />
          </FilterSection>
        )}

        {/* Review Score */}
        <FilterSection
          dataId={buildDataId('reviewScore')}
          title={formatMessage(filterMessages.reviewScore)}
        >
          <ReviewScoreFilter
            value={filters.reviewScore}
            onChange={handleSingleFilterChange}
          />
        </FilterSection>

        {/* Star rating */}
        <FilterSection
          dataId={buildDataId('hotelStarClass')}
          title={formatMessage(filterMessages.hotelStarClass)}
        >
          <StarRatingFilter
            value={filters.starRatings}
            onChange={handleMultiFilterChange}
          />
        </FilterSection>
        {/* Property type */}
        <FilterSection
          dataId={buildDataId('propertyType')}
          title={formatMessage(filterMessages.propertyType)}
          hasShowMore
        >
          <PropertyTypes
            filters={filters.propertyTypes}
            appliedFilters={appliedFilters.propertyTypes}
            toggleMultiFilter={toggleMultiFilter}
            setMultiFilters={setMultiFilters}
            removeMultiFilters={removeMultiFilters}
          />
        </FilterSection>

        {/* Neighborhood */}
        {neighborhoods?.length > 0 && (
          <FilterSection
            dataId={buildDataId('neighborhood')}
            title={formatMessage(filterMessages.neighborhood)}
            hasShowMore
          >
            <NeighborhoodFilter
              neighborhoods={neighborhoods}
              selectedNeighborhoodIds={toArray(filters.neighborhoodIds)}
              onChange={toggleMultiFilter}
              variant={isMobile ? 'one-column' : 'two-columns'}
              limit={isMobile ? 3 : 6}
            />
          </FilterSection>
        )}

        {/* Property brand */}
        {propertyBrands?.length > 0 && (
          <FilterSection
            dataId={buildDataId('propertyBrand')}
            title={formatMessage(filterMessages.propertyBrand)}
            hasShowMore
          >
            <PropertyBrandFilter
              brands={propertyBrands}
              selectedBrandIds={toArray(filters.brandIds)}
              onChange={toggleMultiFilter}
              variant={isMobile ? 'one-column' : 'two-columns'}
              limit={isMobile ? 3 : 6}
            />
          </FilterSection>
        )}

        {/* Traveler experience */}
        <FilterSection
          title={formatMessage(filterMessages.travelerExperience)}
          dataId={buildDataId(themesKey)}
          hasShowMore
          withDivider={false}
        >
          <FacetsRenderCheckboxList
            facet="themes"
            filters={filters.themes}
            appliedFilters={appliedFilters.themes}
            toggleMultiFilter={toggleMultiFilter}
            limit={isMobile ? 3 : 6}
            hasAppliedFilters={hasAppliedFilters}
          />
        </FilterSection>
      </SectionTracker>
    </div>
  )
}
